import "./Admins.scss";
import { useEffect, useState } from "react";
import GreenButton from "../../components/GreenButton/GreenButton";
import BlueButton from "../../components/BlueButton/BlueButton";
import EditUser from "./../../assets/edit-user.svg";
import DeleteUser from "./../../assets/delete.svg";
import AddGif from "./../../assets/add-gif.gif";
import { TextField, useMediaQuery } from "@mui/material";
import InputAdornment from "@mui/material/InputAdornment";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import { AdminsColumns, AdminsPermissionsColumns } from "../../SourceData";
import SwitchButton from "../../components/SwitchButton/SwitchButton";
import MessageModals from "../../components/MessageModals/MessageModals";
import SwipeableModals from "./SwipeableModals/SwipeableModals";
import api from "../../utils/api";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { usePermissions } from "./../../hooks/usePermissions";

const Admins = () => {
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width:767px)");
  const isTablet = useMediaQuery("(min-width: 768px) and (max-width: 1024px)");
  const [query, setQuery] = useState("");
  const [view, setView] = useState("admins");
  const [currentModalView, setCurrentModalView] = useState("changePassword");
  const [openDelete, setOpenDelete] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [deletedItem, setDeletedItem] = useState(null);
  const [selectedAdmin, setSelectedAdmin] = useState(null); // New state for selected admin
  const [fetchData, setFetchData] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);

  // Button visibility permissions
  const canShowAdminsButton = hasPermission(11); // Permission to see "المسئولين الفرعيين" button
  const canShowPermissionsButton = hasPermission(17); // Permission to see "الصلاحيات" button

  // Data access permissions
  const canViewAdmins = hasPermission(13); // Permission to view admins data
  const canViewPermissions = hasPermission(22); // Permission to view permissions data

  // Permission helpers
  const getPermissions = () => {
    if (view === "admins") {
      // المسئولين الفرعيين permissions
      return {
        add: 12, // اضافة
        view: 13, // رؤية
        edit: 14, // تعديل
        delete: 15, // حذف
        activate: 16, // تفعيل
      };
    } else {
      // الصلاحيات permissions
      return {
        add: 20, // اضافة
        view: 22, // رؤية
        edit: 19, // تعديل
        delete: 21, // حذف
        activate: 18, // تفعيل
      };
    }
  };

  // Handle view change with permission validation
  const handleViewChange = (newView) => {
    const dataPermissions = newView === "admins" ? { view: 13 } : { view: 22 };

    if (!hasPermission(dataPermissions.view)) {
      toast.error(
        `ليس لديك صلاحية لعرض ${
          newView === "admins" ? "المسئولين الفرعيين" : "الصلاحيات"
        }`
      );
      return;
    }

    setView(newView);
  };

  // Initialize default view based on available permissions
  useEffect(() => {
    // Set default view based on available permissions
    if (canShowAdminsButton && canViewAdmins) {
      setView("admins");
    } else if (canShowPermissionsButton && canViewPermissions) {
      setView("permissions");
    } else if (canViewAdmins) {
      setView("admins");
    } else if (canViewPermissions) {
      setView("permissions");
    }
  }, [
    canShowAdminsButton,
    canShowPermissionsButton,
    canViewAdmins,
    canViewPermissions,
  ]);

  useEffect(() => {
    const fetchDataFromAPI = async () => {
      const permissions = getPermissions();
      // Check view permission before fetching data
      if (!hasPermission(permissions.view)) {
        return;
      }

      setLoading(true);
      try {
        const response = await api.get(
          view === "admins" ? "/subAdmins" : "/credentials"
        );

        setData(response.data.data);
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("حدث خطأ أثناء تحميل البيانات");
      } finally {
        setLoading(false);
      }
    };

    fetchDataFromAPI();
  }, [view, fetchData]);

  const handleActive = async (id) => {
    const permissions = getPermissions();
    // Check activation permission
    if (!hasPermission(permissions.activate)) {
      toast.error(
        `ليس لديك صلاحية لتفعيل/إلغاء تفعيل ${
          view === "admins" ? "المسئولين الفرعيين" : "الصلاحيات"
        }`
      );
      return;
    }

    setLoading(true);
    try {
      await api.patch(
        view === "admins"
          ? `/subAdmins/${id}/toggleState`
          : `/credentials/${id}/toggleState`
      );
      toast.success("تم تغيير الحالة بنجاح");
      setFetchData(!fetchData);
    } catch (error) {
      console.error("Error toggling status:", error);
      toast.error("حدث خطأ أثناء تغيير الحالة");
    } finally {
      setLoading(false);
    }
  };

  // Updated handleOpen function to accept admin data with permission check
  const handleOpen = (modalView, adminData = null) => {
    const permissions = getPermissions();

    // Check permissions based on the modal view
    if (modalView === "createAdmin" && !hasPermission(permissions.add)) {
      toast.error(
        `ليس لديك صلاحية لإضافة ${
          view === "admins" ? "المسئولين الفرعيين" : "الصلاحيات"
        }`
      );
      return;
    }
    if (modalView === "editAdmin" && !hasPermission(permissions.edit)) {
      toast.error(
        `ليس لديك صلاحية لتعديل ${
          view === "admins" ? "المسئولين الفرعيين" : "الصلاحيات"
        }`
      );
      return;
    }

    setCurrentModalView(modalView);
    setSelectedAdmin(adminData); // Set the selected admin for editing
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
    setSelectedAdmin(null); // Clear selected admin when closing
  };

  const handleEditClick = (rowData) => {
    const permissions = getPermissions();

    if (view === "permissions") {
      // Check edit permission before navigation
      if (!hasPermission(permissions.edit)) {
        toast.error("ليس لديك صلاحية لتعديل الصلاحيات");
        return;
      }
      navigate(`/admins/permissions/${rowData._id}`);
    } else {
      // For admins, the permission check is in handleOpen
      handleOpen("editAdmin", rowData);
    }
  };

  const handleDeleteClick = (rowData) => {
    const permissions = getPermissions();
    // Check delete permission
    if (!hasPermission(permissions.delete)) {
      toast.error(
        `ليس لديك صلاحية لحذف ${
          view === "admins" ? "المسئولين الفرعيين" : "الصلاحيات"
        }`
      );
      return;
    }

    setDeletedItem(rowData);
    setOpenDelete(true);
  };

  const handleAddClick = () => {
    const permissions = getPermissions();

    if (view === "admins") {
      // Check add permission for admins
      if (!hasPermission(permissions.add)) {
        toast.error("ليس لديك صلاحية لإضافة المسئولين الفرعيين");
        return;
      }
      handleOpen("createAdmin");
    } else {
      // Check add permission for permissions
      if (!hasPermission(permissions.add)) {
        toast.error("ليس لديك صلاحية لإضافة الصلاحيات");
        return;
      }
      navigate("/admins/permissions");
    }
  };

  const actionColumn = [
    {
      field: "isActive",
      headerName: "الحالة",
      minWidth: 60,
      headerAlign: "right",
      align: "left",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        const isActive = params?.row?.isActive === true;
        const permissions = getPermissions();
        const canActivate = hasPermission(permissions.activate);

        return (
          <SwitchButton
            checked={isActive}
            onClick={() => handleActive(params.row._id)}
            disabled={!canActivate}
            style={{
              cursor: canActivate ? "pointer" : "not-allowed",
              opacity: canActivate ? 1 : 0.5,
            }}
            title={
              canActivate
                ? `تفعيل/إلغاء تفعيل ${
                    view === "admins" ? "المسئول" : "الصلاحية"
                  }`
                : `ليس لديك صلاحية لتفعيل/إلغاء تفعيل ${
                    view === "admins" ? "المسئولين الفرعيين" : "الصلاحيات"
                  }`
            }
          />
        );
      },
    },
    {
      field: "action",
      headerName: "",
      width: 100,
      headerAlign: "center",
      align: "center",
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const permissions = getPermissions();
        const canEdit = hasPermission(permissions.edit);
        const canDelete = hasPermission(permissions.delete);

        return (
          <div className="cell-action">
            <div
              className="cell-btn"
              onClick={() => handleEditClick(params.row)}
              style={{
                cursor: canEdit ? "pointer" : "not-allowed",
                opacity: canEdit ? 1 : 0.5,
              }}
              title={
                canEdit
                  ? `تعديل ${view === "admins" ? "المسئول" : "الصلاحية"}`
                  : `ليس لديك صلاحية لتعديل ${
                      view === "admins" ? "المسئولين الفرعيين" : "الصلاحيات"
                    }`
              }
            >
              <img src={EditUser} alt="تعديل" />
            </div>

            <div
              className="cell-btn"
              onClick={() => handleDeleteClick(params.row)}
              style={{
                cursor: canDelete ? "pointer" : "not-allowed",
                opacity: canDelete ? 1 : 0.5,
              }}
              title={
                canDelete
                  ? `حذف ${view === "admins" ? "المسئول" : "الصلاحية"}`
                  : `ليس لديك صلاحية لحذف ${
                      view === "admins" ? "المسئولين الفرعيين" : "الصلاحيات"
                    }`
              }
            >
              <img src={DeleteUser} alt="حذف" />
            </div>
          </div>
        );
      },
    },
  ];

  // If user is trying to view a section they don't have permission for
  const permissions = getPermissions();
  if (!hasPermission(permissions.view)) {
    return (
      <div className="admins">
        <div className="admins-header">
          <p className="title">المسؤولين الفرعيين</p>
          <div className="btns-container">
            {canShowAdminsButton && (
              <GreenButton
                text="المسؤولين الفرعيين"
                size="medium"
                variant={view === "admins" ? "fill" : "outlined"}
                onClick={() => handleViewChange("admins")}
                disabled={!canViewAdmins}
              />
            )}

            {canShowPermissionsButton && (
              <GreenButton
                text="الصلاحيات"
                size="medium"
                variant={view === "permissions" ? "fill" : "outlined"}
                onClick={() => handleViewChange("permissions")}
                disabled={!canViewPermissions}
              />
            )}
          </div>
        </div>
        <div
          className="container"
          style={{ padding: "2rem", textAlign: "center" }}
        >
          <p>
            ليس لديك صلاحية لعرض{" "}
            {view === "admins" ? "المسئولين الفرعيين" : "الصلاحيات"}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="admins">
      <div className="admins-header">
        <p className="title">المسؤولين الفرعيين</p>

        <div className="btns-container">
          {canShowAdminsButton && (
            <GreenButton
              text="المسؤولين الفرعيين"
              size="medium"
              variant={view === "admins" ? "fill" : "outlined"}
              onClick={() => handleViewChange("admins")}
              disabled={!canViewAdmins}
            />
          )}
          {canShowPermissionsButton && (
            <GreenButton
              text="الصلاحيات"
              size="medium"
              variant={view === "permissions" ? "fill" : "outlined"}
              onClick={() => handleViewChange("permissions")}
              disabled={!canViewPermissions}
            />
          )}
        </div>

        <div className="actions-container">
          <div className="search">
            <TextField
              id="outlined-search"
              type="search"
              value={query}
              placeholder={
                view === "admins"
                  ? "ابحث بأسم المسؤول / البريد الالكتروني او رقم الموبايل"
                  : "بحث..."
              }
              onChange={(e) => setQuery(e.target.value)}
              sx={{
                width: isMobile
                  ? "100%"
                  : isTablet || view === "permissions"
                  ? "300px"
                  : "616px",
                borderRadius: "8px",
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </div>

          {hasPermission(permissions.add) &&
            (view === "admins" ? (
              <BlueButton
                text="اضافة مسؤول"
                size={isMobile ? "full" : isTablet ? "fit" : "medium"}
                variant="fill"
                onClick={handleAddClick}
                icon={<img src={AddGif} alt="" />}
              />
            ) : (
              <BlueButton
                text="اضافة صلاحية جديدة"
                size={isMobile ? "full" : isTablet ? "fit" : "large"}
                variant="fill"
                onClick={handleAddClick}
                icon={<img src={AddGif} alt="" />}
              />
            ))}
        </div>
      </div>
      <div className="container">
        <DataGrid
          sx={{ backgroundColor: "white", width: "100%" }}
          autoHeight={true}
          columns={
            view === "admins"
              ? AdminsColumns.concat(actionColumn)
              : AdminsPermissionsColumns.concat(actionColumn)
          }
          rows={data.map((row, index) => {
            return { ...row, index: index + 1 };
          })}
          getRowId={(row) => row._id}
          disableVirtualization
          disableColumnFilter
          disableColumnMenu
          hideFooter
          rowHeight={60}
          loading={loading}
        />
      </div>

      {openDelete && hasPermission(permissions.delete) && (
        <MessageModals
          open={openDelete}
          currentView="delete"
          deleteMessage={
            view === "admins" ? "تم حذف المسؤل بنجاح" : "تم حذف الصلاحية بنجاح"
          }
          route={
            view === "admins"
              ? `/subAdmins/${deletedItem?._id}`
              : `/credentials/${deletedItem?._id}`
          }
          onClose={() => {
            setOpenDelete(false);
            setDeletedItem(null);
          }}
          onDataChange={() => setFetchData(!fetchData)}
        />
      )}

      {isOpen &&
        ((currentModalView === "createAdmin" &&
          hasPermission(permissions.add)) ||
          (currentModalView === "editAdmin" &&
            hasPermission(permissions.edit)) ||
          currentModalView === "changePassword") && (
          <SwipeableModals
            open={isOpen}
            onClose={handleClose}
            onOpen={handleOpen}
            currentView={currentModalView}
            selectedAdmin={selectedAdmin} // Pass the selected admin
            onDataChange={() => setFetchData(!fetchData)} // Pass callback to refresh data
          />
        )}
    </div>
  );
};

export default Admins;
